import React, { useState } from 'react';
import { object, string } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { obfuscatedCoordinates } from '../../util/maps';
import { venueNameFromPublicData } from '../../util/listingSeoCopy';
import { buildStay22EmbedUrl, stay22ConfigFromEnv } from '../../util/stay22Embed';
import { Heading, Map } from '../../components';

import css from './ListingPage.module.css';

const resolveStay22MapUrl = ({ geolocation, publicData }) => {
  const { enabled, aid, campaign, mainColor } = stay22ConfigFromEnv();
  if (!enabled || !aid) {
    return null;
  }
  const venueName = venueNameFromPublicData(publicData);
  const address =
    String(publicData?.location?.address || '').trim() ||
    [publicData?.city, publicData?.state].filter(Boolean).join(', ') ||
    null;

  return buildStay22EmbedUrl({
    aid,
    lat: geolocation?.lat,
    lng: geolocation?.lng,
    address,
    venueName,
    mainColor,
    campaign: [campaign || 'listing-location', 'listing-page'],
  });
};

/**
 * Listing location section: Stay22 places-to-stay map when configured,
 * otherwise the legacy Mapbox static/interactive map.
 */
const SectionMapMaybe = props => {
  const { className, rootClassName, geolocation, publicData, listingId, mapsConfig } = props;
  const [isStatic, setIsStatic] = useState(true);

  const stay22Url = resolveStay22MapUrl({ geolocation, publicData });
  const venueName = venueNameFromPublicData(publicData) || 'the venue';

  if (!geolocation && !stay22Url) {
    return null;
  }

  const classes = classNames(rootClassName || css.sectionMap, className);

  return (
    <div className={classes}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.locationTitle" />
      </Heading>
      {stay22Url ? (
        <iframe
          title={`Location and places to stay near ${venueName}`}
          className={css.stay22Map}
          src={stay22Url}
          width="100%"
          height="460"
          frameBorder="0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        (() => {
          const address = publicData && publicData.location ? publicData.location.address : '';
          const cacheKey = listingId
            ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}`
            : null;
          const mapProps = mapsConfig.fuzzy.enabled
            ? {
                obfuscatedCenter: obfuscatedCoordinates(
                  geolocation,
                  mapsConfig.fuzzy.offset,
                  cacheKey
                ),
              }
            : { address, center: geolocation };
          const map = <Map {...mapProps} useStaticMap={isStatic} />;
          return isStatic ? (
            <button className={css.map} type="button" onClick={() => setIsStatic(false)}>
              {map}
            </button>
          ) : (
            <div className={css.map}>{map}</div>
          );
        })()
      )}
    </div>
  );
};

SectionMapMaybe.defaultProps = {
  rootClassName: null,
  className: null,
  geolocation: null,
  listingId: null,
  publicData: null,
  mapsConfig: { fuzzy: { enabled: false, offset: 0 } },
};

SectionMapMaybe.propTypes = {
  rootClassName: string,
  className: string,
  geolocation: propTypes.latlng,
  listingId: propTypes.uuid,
  publicData: object,
  mapsConfig: object,
};

export default SectionMapMaybe;
